/**
 * Global canonical link.
 *
 * The site is client-rendered, so we set a self-referencing canonical to the
 * non-www https origin for the current path (Googlebot renders JS and reads the
 * head). This is what consolidates the www / non-www / http duplicates Search
 * Console flagged. Per-page title/description/OG are handled by useDocumentMeta.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SEO_ORIGIN = 'https://darak.online';

/**
 * Emit hreflang alternates linking the English and Arabic versions of a page,
 * so Google serves each language its own version and treats them as one work
 * rather than duplicates. Also sets <html lang/dir> so the Arabic page renders
 * right-to-left. Pass the two paths (e.g. '/' and '/ar').
 *
 * The site is client-rendered; Googlebot renders JS and reads these, and the
 * sitemap carries the same alternates as the stronger signal.
 */
export function useHreflang(enPath: string, arPath: string, current: 'en' | 'ar') {
  const enHref = `${SEO_ORIGIN}${enPath}`;
  const arHref = `${SEO_ORIGIN}${arPath}`;
  useEffect(() => {
    const set = (hreflang: string, href: string) => {
      const sel = `link[rel="alternate"][hreflang="${hreflang}"]`;
      let el = document.head.querySelector<HTMLLinkElement>(sel);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    set('en', enHref);
    set('ar', arHref);
    set('x-default', enHref);

    const html = document.documentElement;
    const prevLang = html.lang;
    const prevDir = html.getAttribute('dir');
    html.lang = current === 'ar' ? 'ar' : 'en';
    html.setAttribute('dir', current === 'ar' ? 'rtl' : 'ltr');

    return () => {
      html.lang = prevLang || 'en';
      if (prevDir) html.setAttribute('dir', prevDir);
      else html.removeAttribute('dir');
    };
  }, [enHref, arHref, current]);
}

/**
 * Always-on canonical for the current route. Call once, high in the tree.
 * Query strings are dropped so `/search?x=1` canonicalizes to `/search`.
 */
export function useCanonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    const href = `${SEO_ORIGIN}${pathname}`;
    let link = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }, [pathname]);
}
